import { ExternalLink } from 'lucide-react';
import clsx from 'clsx';
import { useWizardStore } from '../../stores/wizardStore';
import { useWizardOptions } from '../../hooks/useWizardOptions';

export default function PluginsStep() {
  const { plugins, updatePlugins } = useWizardStore();
  const { options, loading } = useWizardOptions();

  const availablePlugins = options?.plugins?.available ?? [];

  if (loading) {
    return (
      <div className="text-gray-500 text-sm py-8 text-center">
        Loading configuration...
      </div>
    );
  }

  const togglePlugin = (name: string) => {
    if (plugins.includes(name)) {
      updatePlugins(plugins.filter((p) => p !== name));
    } else {
      updatePlugins([...plugins, name]);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">
          Community Plugins
        </h2>
        <p className="text-sm text-gray-500">
          Enable community plugins to extend your AI development blueprint
          with additional workflows and tools. Selected plugins are
          pre-configured in your generated project.
        </p>
      </div>

      {availablePlugins.length === 0 ? (
        <div className="text-center py-12 text-gray-600 text-sm">
          No community plugins available yet.
        </div>
      ) : (
        <div className="space-y-3">
          {availablePlugins.map((plugin) => {
            const isSelected = plugins.includes(plugin.name);
            return (
              <div
                key={plugin.name}
                className={clsx(
                  'flex items-start justify-between p-5 rounded-lg border transition-all duration-200',
                  isSelected
                    ? 'bg-accent/10 border-accent/30'
                    : 'bg-surface-DEFAULT border-dark-border hover:border-surface-300'
                )}
              >
                <div className="flex-1 mr-4">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-white">
                      {plugin.displayName}
                    </h3>
                    <span className="px-2 py-0.5 rounded bg-surface-100 border border-dark-border text-[10px] text-gray-500 uppercase tracking-wider">
                      {plugin.category}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 mb-2">
                    {plugin.description}
                  </p>
                  <a
                    href={`https://github.com/${plugin.repo}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-accent-300 hover:text-accent-200 transition-colors"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {plugin.repo}
                  </a>
                </div>

                <button
                  onClick={() => togglePlugin(plugin.name)}
                  className={clsx(
                    'relative w-11 h-6 rounded-full transition-colors duration-200 shrink-0 mt-1',
                    isSelected ? 'bg-accent' : 'bg-surface-300'
                  )}
                >
                  <div
                    className={clsx(
                      'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200',
                      isSelected && 'translate-x-5'
                    )}
                  />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {plugins.length > 0 && (
        <div className="p-3 rounded-lg bg-surface-DEFAULT border border-dark-border">
          <span className="text-xs text-gray-500">Selected: </span>
          <span className="text-xs text-gray-300">
            {plugins.join(', ')}
          </span>
        </div>
      )}
    </div>
  );
}
